import { z } from 'zod';
import { getRevalidateTime } from '../../../utils/cache';
import { getEnvVar } from '../../../utils/env';

const PUBLISHER_ID = getEnvVar('NEXT_PUBLIC_PUBLISHER_ID');
const DEFAULT_CATEGORY_SORT_BY = getEnvVar(
  'NEXT_PUBLIC_DEFAULT_CATEGORY_SORT_BY',
);

// TODO: ensure selected language is always put into header 'Accept-Language' when fetching categories

const ImageTemplates = z.object({
  regions: z.array(z.string()),
  locations: z.array(z.string()),
  withRegion: z.string(),
  withLocation: z.string(),
  complete: z.string(),
});

const Images = z.object({
  defaultUrl: z.string(),
  version: z.string(),
  templates: ImageTemplates,
});

const Publishing = z.object({
  start: z.string(),
  platform: z.string(),
  geoExpressions: z.array(z.string()),
});

const Product = z.object({
  id: z.string(),
  name: z.string(),
  accessType: z.string(),
});

export type ProductType = z.infer<typeof Product>;

const Child = z.object({
  id: z.string(),
  timestamp: z.string(),
  images: Images.optional(),
  categoryType: z.string(),
  parentId: z.string(),
  path: z.string(),
  children: z.array(z.unknown()),
  publishing: Publishing,
  products: z.array(Product),
  'publisher-id': z.number().optional(),
  'description-short': z.string().optional(),
  title: z.string().optional(), // not always present for all languages
});

export const Category = z.object({
  id: z.string(),
  timestamp: z.string(),
  images: Images.optional(),
  categoryType: z.string(),
  parentId: z.string().optional(),
  path: z.string(),
  children: z.array(Child),
  publishing: Publishing,
  products: z.array(Product),
  'publisher-id': z.number().optional(),
  'description-short': z.string().optional(),
  title: z.string().optional(), // not always present for all languages
  people: z.string().optional(),
  tags: z.string().optional(),
  labels: z.string().optional(),
  season: z.number().optional(),
  'age-rating': z.string().optional(),
  'reference-links': z.string().optional(),
});

export type CategoryType = z.infer<typeof Category>;

const ErrorInfo = z.object({
  title: z.string(),
  detail: z.string(),
  code: z.string(),
});

const categoriesResponseValidator = z.object({
  data: z.array(Category).optional(),
  errors: z.array(ErrorInfo).optional(),
});

type NextFetchRequestInit = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

export const getCategory = async ({
  categoryId,
  locale,
}: {
  categoryId: string;
  locale?: string;
}) => {
  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: NextFetchRequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
    cache: 'reload', // Force client-side revalidation with server
    next: {
      revalidate: getRevalidateTime(),
      tags: locale
        ? [`category-${categoryId}-${locale}`]
        : [`category-${categoryId}`],
    },
  };
  const queryParams =
    'depth=2' +
    (DEFAULT_CATEGORY_SORT_BY ? `&sort=${DEFAULT_CATEGORY_SORT_BY}` : '');
  const categoryCDurl = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/categories/${categoryId}?${queryParams}`;

  return fetch(categoryCDurl, requestOptions)
    .then((res) => res.json())
    .then((json) => {
      const response = categoriesResponseValidator.parse(json);
      return response.data ? response.data[0] : undefined;
    });
};

export const getCategories = async ({
  categoryIds,
  locale,
}: {
  categoryIds: string[];
  locale?: string;
}) => {
  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };

  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  if (categoryIds?.length === 0) {
    return Promise.resolve(undefined);
  }

  const query =
    `id:(${categoryIds.join(' ')})` +
    (PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '');

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar(
      'NEXT_PUBLIC_ENVIRONMENT',
    )}.vmnd.tv/api/v1/categories?query=${query})`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = categoriesResponseValidator.parse(json);
      return response.data;
    });
};

export const searchCategories = async (query: string) => {
  const headers = {
    accept: 'application/json',
  };
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };

  const companyQuery = PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '';

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/categories?query="${query}"~ AND categoryType:(SHOW,SPORT,NEWS_PROGRAM)${companyQuery}&page[size]=50`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = categoriesResponseValidator.parse(json);
      if (response.errors) {
        throw new Error(response.errors[0]?.detail);
      }
      return response.data;
    });
};
